import { type ParticipantDocument, ParticipantDocumentStatus } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import type { Editor } from '@tiptap/react';
import React from 'react';
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarRightCollapse } from 'react-icons/tb';
import { HocuspocusDebugMenu } from '../../components/tiptap/HocuspocusDebugMenu';
import type { HocuspocusState } from '../../components/tiptap/hooks/useHocuspocusProvider';
import { SaveStatusIndicator } from '../../components/tiptap/SaveStatusIndicator';
import { UserStatusIndicator } from '../../components/tiptap/UserStatusIndicator';
import { ParticipantDocumentActionMenu } from './ParticipantDocumentActionMenu';

interface Props {
  editor: Editor;
  participantDocument: ParticipantDocument;
  state: HocuspocusState;
  isSidebarOpen: boolean;
  toggleSidebarOpen: () => void;
}

export const ParticipantDocumentTitleBar = (props: Props) => {
  const { editor, participantDocument, state, isSidebarOpen, toggleSidebarOpen } = props;

  const isFinalized = participantDocument.status === ParticipantDocumentStatus.Finalized;

  return (
    <ChakraV3.Stack gap={4} mt={4}>
      <ChakraV3.HStack>
        <ChakraV3.Heading size='3xl' fontWeight='bold' lineClamp={1}>
          {participantDocument.name}
        </ChakraV3.Heading>
        <ChakraV3.Spacer />

        <ChakraV3.HStack flexShrink={0}>
          <SaveStatusIndicator state={state} />
          <UserStatusIndicator editor={editor} state={state} />
          <HocuspocusDebugMenu variant='outline' size='sm' />
          <ParticipantDocumentActionMenu
            participantDocument={participantDocument}
            variant='outline'
            size='sm'
          />
          <ChakraV3.Tooltip openDelay={2000} content='Toggle document sidebar'>
            <ChakraV3.IconButton
              onClick={toggleSidebarOpen}
              aria-label='sidebar'
              size='sm'
              variant='ghost'
              color='gray.700'
            >
              {isSidebarOpen ? <TbLayoutSidebarRightCollapse /> : <TbLayoutSidebarLeftCollapse />}
            </ChakraV3.IconButton>
          </ChakraV3.Tooltip>
        </ChakraV3.HStack>
      </ChakraV3.HStack>

      {isFinalized && (
        <ChakraV3.AlertBanner
          status='info'
          title='This document is currently finalized. It is now read-only and cannot be edited. If you would like to make changes, you must change the status from Finalized.'
        />
      )}
    </ChakraV3.Stack>
  );
};
